import { Dialog, DialogTitle, DialogContent, Box, FormControl, RadioGroup, FormControlLabel, Radio, DialogActions, Button, Paper, Stack, Typography, Backdrop, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import { OutsourcingContractPdfGenerationSetting, OutsourcingContractPdfPreviewRequestEntity, OutsourcingContractPdfRequestEntity } from 'src/entities/OutsourcingContractPdf.entity';
import { useMutationOutsourcingContractPdf } from 'src/hooks/useOutsourcingContractPdf.request';
import { useOutsourcingContractSettingQuery } from 'src/hooks/useOutsourcingContractSetting.request';
import stringUtil from 'src/utils/string.util';

import DebouncedTextField from '../OutsourcingContractSettingsPresenter/DebouncedTextFieldProps';

type Action = { type: 'SET_FIELD', key: keyof OutsourcingContractPdfGenerationSetting, orderId: number, value: string | File | boolean };

const formReducer = (state: Record<number, OutsourcingContractPdfGenerationSetting>, action: Action): Record<number, OutsourcingContractPdfGenerationSetting> => {
  switch (action.type) {
    case 'SET_FIELD': {
      state[action.orderId] = {
        ...state[action.orderId],
        [action.key]: action.value,
      };
      return { ...state };
    }
    default:
      return state;
  }
};

type Props = {
  downloadOutsourcingPdfSettingsIsOpen: boolean;
  handlePdfSettingsClose: () => void;
  orderIds: number[];
};

const PdfSettingDialog: FC<Props> = memo(({
  downloadOutsourcingPdfSettingsIsOpen,
  handlePdfSettingsClose,
  orderIds,
}) => {
  const licenseContext = useContext(LicenseContext);
  const { enqueueSnackbar } = useSnackbar();

  const { data: setting, isLoading: settingIsLoading } = useOutsourcingContractSettingQuery(licenseContext.config?.selected_company_id || licenseContext.config?.company_id, downloadOutsourcingPdfSettingsIsOpen);
  const { generate, create } = useMutationOutsourcingContractPdf();

  const [currentOrderId, setCurrentOrderId] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [pdfGenerationType, setPdfGenerationType] = useState<'single' | 'multiple'>('single');
  const [previewHtml, setPreviewHtml] = useState<string>('');
  // const [contentHeight, setContentHeight] = useState<number>(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const leftStackRef = useRef<HTMLDivElement>(null);
  const defaultSettings: OutsourcingContractPdfGenerationSetting = useMemo(() => ({
    companyId: licenseContext.config?.selected_company_id || licenseContext.config?.company_id,
    introduction: '',
    aboutUnitPrice: '',
    paymentTerm: '',
    companyName: '',
    companyAddress: '',
    companyPhoneNo: '',
    companyFaxNo: '',
    freeTextOneTitle: '',
    freeTextOneContent: '',
    freeTextTwoTitle: '',
    freeTextTwoContent: '',
    freeTextThreeContent: '',
    freeTextFourContent: '',
    imageFile: null,
    imageUrl: null,
    branchName: '',
    contactPerson: '',
    requiredMaterials: '',
    hasAttachment: false,
  }), [licenseContext.config]);

  useEffect(() => {
    setCurrentOrderId(orderIds[0]);
  }, [orderIds, downloadOutsourcingPdfSettingsIsOpen]);

  const [formState, dispatch] = useReducer(formReducer, {});
  const [debouncedState, dispatchDebounced] = useReducer(formReducer, {});

  useEffect(() => {
    if (!downloadOutsourcingPdfSettingsIsOpen) return;
    if (settingIsLoading) return;
    if (!setting) return;

    const defaultValues = { ...defaultSettings, ...setting };

    orderIds.forEach((orderId) => {
      Object.keys(defaultValues).forEach((key: keyof OutsourcingContractPdfGenerationSetting) => {
        dispatch({ type: 'SET_FIELD', key, orderId, value: defaultValues[key] as string | boolean | File });
        dispatchDebounced({ type: 'SET_FIELD', key, orderId, value: defaultValues[key] as string | boolean | File });
      });
    });
  }, [defaultSettings, orderIds, setting, settingIsLoading, downloadOutsourcingPdfSettingsIsOpen]);

  useEffect(() => {
    if (!downloadOutsourcingPdfSettingsIsOpen) return;

    const generatePreview = async () => {
      if (settingIsLoading || !debouncedState[currentOrderId]) {
        setPreviewHtml('');
        return;
      }
      const entity: OutsourcingContractPdfPreviewRequestEntity = {
        outsourcingContractSettings: debouncedState[currentOrderId],
        currentOrderId,
        outsourcingContractSettingChange: null,
        preview: true,
      };

      const result = await create.mutateAsync(entity);
      setPreviewHtml(String(result.data));
    };

    // eslint-disable-next-line no-void
    void generatePreview();
  }, [currentOrderId, debouncedState, settingIsLoading, downloadOutsourcingPdfSettingsIsOpen]);

  // localStorage からの初期値読み込み
  useEffect(() => {
    const savedType = localStorage.getItem('pdfGenerationType') as 'single' | 'multiple';
    if (savedType) {
      setPdfGenerationType(savedType);
    }
  }, []);

  // pdfGenerationType が変更されたら localStorage に保存
  useEffect(() => {
    localStorage.setItem('pdfGenerationType', pdfGenerationType);
  }, [pdfGenerationType]);

  const handlePdfSettingsSubmit = async () => {
    const entity: OutsourcingContractPdfRequestEntity = {
      outsourcingContractSettings: debouncedState,
      orderIds,
      preview: false,
      pdfGenerationType,
    };

    setGenerating(true);
    const result = await generate.mutateAsync(entity, {
      onError: () => {
        enqueueSnackbar('エラーが発生しました。', { variant: 'error' });
      },
      onSettled: () => {
        setGenerating(false);
      }
    });

    // Content-Dispositionヘッダーからファイル名を取得
    const contentDisposition = String(result.headers['content-disposition']);
    let fileName = '';

    if (contentDisposition) {
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
      if (matches != null && matches[1]) {
        fileName = matches[1].replace(/['"]/g, '');
      }
    }

    if (!fileName) {
      fileName = `outsourcing_contract_${new Date().getTime()}.${pdfGenerationType === 'single' ? 'pdf' : 'zip'}`;
    }

    // Blobを作成
    const blob = new Blob([result.data as unknown as ArrayBuffer], {
      type: pdfGenerationType === 'single'
        ? 'application/pdf'
        : 'application/zip'
    });

    // ダウンロードリンクを作成して自動クリック
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    handlePdfSettingsClose();
  };

  type InputFieldMeta = {
    key: keyof OutsourcingContractPdfGenerationSetting;
    label: string;
    rows?: number;
    minRows?: number;
    type?: string;
  };

  const inputFields: InputFieldMeta[] = [
    {
      key: 'companyName',
      label: '会社名',
      rows: 1,
    },
    {
      key: 'branchName',
      label: '支社名',
      rows: 1,
    },
    {
      key: 'contactPerson',
      label: '担当者',
      rows: 1,
    },
    {
      key: 'companyAddress',
      label: '住所',
      rows: 1,
    },
    {
      key: 'companyPhoneNo',
      label: '電話番号',
      rows: 1,
    },
    {
      key: 'companyFaxNo',
      label: 'FAX番号',
      rows: 1,
    },
    {
      key: 'introduction',
      label: 'はじめに',
      minRows: 4,
    },
    {
      key: 'hasAttachment',
      label: '添付資料',
      type: 'radio',
    },
    {
      key: 'requiredMaterials',
      label: '必要資材',
      minRows: 4,
    },
    {
      key: 'aboutUnitPrice',
      label: '単価について',
      rows: 1,
    },
    {
      key: 'paymentTerm',
      label: '支払条件',
      minRows: 4,
    },
    {
      key: 'freeTextOneTitle',
      label: 'フリーテキスト1 タイトル',
      rows: 1,
    },
    {
      key: 'freeTextOneContent',
      label: 'フリーテキスト1 内容',
      minRows: 3,
    },
    {
      key: 'freeTextTwoTitle',
      label: 'フリーテキスト2 タイトル',
      rows: 1,
    },
    {
      key: 'freeTextTwoContent',
      label: 'フリーテキスト2 内容',
      minRows: 3,
    },
    {
      key: 'freeTextThreeContent',
      label: 'フリーテキスト3 内容',
      minRows: 3,
    },
    {
      key: 'freeTextFourContent',
      label: 'フリーテキスト4 内容',
      minRows: 3,
    },
  ];

  // iframeの高さを取得する関数
  const updateIframeHeight = useCallback(() => {
    let tallestHeight = 0;
    if (iframeRef.current) {
      const height = iframeRef.current.contentWindow?.document.body.scrollHeight;
      console.log('iframe height:', height);
      if (height) {
        tallestHeight = height;
      }
    }
    if (leftStackRef.current) {
      const height = leftStackRef.current.scrollHeight;
      console.log('left stack height:', height);
      if (height && tallestHeight < height) {
        tallestHeight = height;
      }
    }
    // setContentHeight(tallestHeight + 32); // padding分を加算
  }, []);

  // プレビューHTMLが更新されたら高さを更新
  useEffect(() => {
    if (previewHtml) {
      // iframeの読み込み完了を待つ
      const timer = setTimeout(updateIframeHeight, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [previewHtml, updateIframeHeight]);

  useEffect(() => {
    if (!settingIsLoading && !setting) {
      enqueueSnackbar('委託表設定がありません。先に設定を作成してください。');
      handlePdfSettingsClose();
    }
  }, [enqueueSnackbar, handlePdfSettingsClose, setting, settingIsLoading]);

  const backdropMemo = useMemo(() => (
    <Backdrop
      sx={{ color: '#fff', zIndex: 100 }}
      open={generating}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ), [generating]);

  return (
    <Dialog
      open={downloadOutsourcingPdfSettingsIsOpen}
      onClose={handlePdfSettingsClose}
      sx={{ p: 0, m: 0, mt: 3, }}
      fullWidth
      fullScreen
    >
      <DialogTitle>
        委託表出力設定
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', p: 0, m: 0, }}>
          <Button
            onClick={() => {
              const currentIndex = orderIds.indexOf(currentOrderId);
              if (currentIndex > 0) {
                setCurrentOrderId(orderIds[currentIndex - 1]);
              }
            }}
            disabled={orderIds.indexOf(currentOrderId) === 0}
          >
            前へ
          </Button>
          <Box>
            {[orderIds.indexOf(currentOrderId) + 1, '/', orderIds.length].join(' ')}
          </Box>
          <Button
            onClick={() => {
              const currentIndex = orderIds.indexOf(currentOrderId);
              if (currentIndex < orderIds.length - 1) {
                setCurrentOrderId(orderIds[currentIndex + 1]);
              }
            }}
            disabled={orderIds.indexOf(currentOrderId) === orderIds.length - 1}
          >
            次へ
          </Button>
        </Box>
        <Box
          sx={{
            height: 'calc(100vh - 205px)',
            overflow: 'auto',
          }}
        >
          <Paper>
            <Stack spacing={2} direction="row" sx={{ width: '100%', p: 2 }} >
              {/* 左側: 入力フォーム */}
              <Stack sx={{ width: '50%' }} >
                <Paper sx={{ height: '100%', }}>
                  <Stack spacing={2} sx={{ m: 0, p: 2 }} ref={leftStackRef}>
                    {formState[currentOrderId] && inputFields.map((field) => (
                      field.type === 'radio' ? (
                        <FormControl key={field.key} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ mb: 1, pr: 2 }}>{field.label}</Typography>
                          <RadioGroup
                            row
                            value={stringUtil.toText(formState[currentOrderId][field.key])}
                            onChange={(e) => {
                              const value = e.target.value === 'true';
                              dispatch({ type: 'SET_FIELD', key: field.key, orderId: currentOrderId, value });
                              dispatchDebounced({ type: 'SET_FIELD', key: field.key, orderId: currentOrderId, value });
                            }}
                          >
                            <FormControlLabel value="true" control={<Radio />} label="有" />
                            <FormControlLabel value="false" control={<Radio />} label="無" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        <DebouncedTextField
                          key={field.key}
                          label={field.label}
                          orderId={currentOrderId}
                          multiline={!(field.rows && field.rows === 1)}
                          size="small"
                          rows={field.rows}
                          minRows={field.minRows}
                          value={stringUtil.toText(formState[currentOrderId][field.key])}
                          keyValue={field.key}
                          onValueChange={(val) => {
                            dispatch({ type: 'SET_FIELD', key: field.key, orderId: currentOrderId, value: val });
                          }}
                          onDebouncedChange={(val) => {
                            dispatchDebounced({ type: 'SET_FIELD', key: field.key, orderId: currentOrderId, value: val });
                          }}
                        />
                      )
                    ))}
                  </Stack>
                </Paper>
              </Stack>
              {/* 右側: プレビュー */}
              <Stack sx={{ width: '50%' }} >
                <Paper sx={{ p: 2, height: '100%', }}>
                  <iframe
                    ref={iframeRef}
                    srcDoc={previewHtml}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    title="preview"
                  />
                </Paper>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ pr: 3 }}>
        {orderIds.length > 1 && (
          <Box>
            <FormControl component="fieldset">
              <RadioGroup
                value={pdfGenerationType}
                onChange={(e) => setPdfGenerationType(e.target.value as 'single' | 'multiple')}
                sx={{ p: 0, m: 0, }}
                row
              >
                <FormControlLabel
                  value="single"
                  control={<Radio />}
                  label="すべてをまとめた1枚のPDFを作成する"
                  sx={{ p: 0, m: 0, }}
                />
                <FormControlLabel
                  value="multiple"
                  control={<Radio />}
                  label="個別のPDFを作成する (ZIPでダウンロードする)"
                  sx={{ p: 0, m: 0, }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}
        <Button onClick={handlePdfSettingsClose}>キャンセル</Button>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <Button onClick={handlePdfSettingsSubmit} variant="contained">作成する</Button>
      </DialogActions>
      {backdropMemo}
    </Dialog>
  );
});

export default PdfSettingDialog;
